import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { Typography, Table, Col } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { resolveMeetingDataProp } from "utils/dpatAssessmentStartData";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";

const { Title, Text } = Typography;

const MEETING_LINK_KEYS = ["docs", "attendance", "recommendation"];

const LINK_LABELS = {
  docs: "View Minutes",
  attendance: "View Attendance",
  recommendation: "View Document",
};

const TEXT_FIELDS = ["invitationDate", "meetingDate", "minutes", "invitationLetterReference"];

function formatOptionalText(value) {
  if (value == null || value === "" || value === "N/A") {
    return "";
  }
  return value;
}

const PRCCMeeting = forwardRef(({
  data,
  year,
  columns,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const displayData = resolveMeetingDataProp({
    isAssessmentStarted,
    savedAssessmentData,
    liveData: data,
  });

  const meetingRows = displayData?.data ?? [];

  const meetingTableColumns = useMemo(
    () => (columns ?? []).map((col) => {
      if (TEXT_FIELDS.includes(col.dataIndex)) {
        return {
          ...col,
          render: (value) => formatOptionalText(value),
        };
      }

      if (MEETING_LINK_KEYS.includes(col.dataIndex)) {
        return {
          ...col,
          render: (value) => renderDpatEvidenceLink(
            value,
            LINK_LABELS[col.dataIndex] ?? "View Document",
          ),
        };
      }

      return col;
    }),
    [columns],
  );

  const DPAT_SLUG = "c2.0-2.4";

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI2",
      area: "Other Statutory Meetings / Requirements",
      data,
    }),
  }));

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`c2.0-2.4-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={displayData?.fulfillment} />
          </Col>

          {assessmentStartLoading && <Text>Loading saved assessment data…</Text>}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of PRCC Meetings</Title>

          {meetingRows.length > 0 ? (
            <Table
              columns={meetingTableColumns}
              dataSource={meetingRows}
              pagination={false}
              bordered
              rowKey={(record, index) => record.key || index}
            />
          ) : (
            <Text>No PRCC meetings data available</Text>
          )}
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default PRCCMeeting;
